"use client";

import type {
	StatusBannerElementOptions,
	StatusBannerType,
} from "@whop/embedded-components-vanilla-js/types";

import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useElementSnapshot } from "../../lib/use-element-snapshot";
import { usePayoutsSession } from "../session";

export function StatusBannerElement({
	options = {},
	className,
	style,
	onReady,
	onVerifyClick,
	onBannerChange,
	fallback,
}: {
	options?: StatusBannerElementOptions;
	className?: string;
	style?: React.CSSProperties;
	onReady?: () => void;
	onVerifyClick?: (ev: CustomEvent) => void;
	onBannerChange?: (ev: CustomEvent, bannerType: StatusBannerType) => void;
	fallback?: React.ReactNode;
}) {
	const payoutsSession = usePayoutsSession();
	const ref = useRef<HTMLDivElement>(null);
	const element = useMemo(() => {
		if (!payoutsSession) return null;
		const createdElement = payoutsSession.createElement(
			StatusBannerElement,
			options,
		);
		return createdElement;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [payoutsSession]);

	const elementSnapshot = useElementSnapshot(element);

	const isReady = elementSnapshot?.state === "ready";

	useEffect(() => {
		if (!element) return;
		element.updateOptions(options);
	}, [options, element]);

	useEffect(() => {
		if (!element || !onReady) return;
		element.on("ready", onReady);
		return () => {
			element.off("ready", onReady);
		};
	}, [element, onReady]);

	useEffect(() => {
		if (!element || !onVerifyClick) return;
		element.on("verifyClick", onVerifyClick);
		return () => {
			element.off("verifyClick", onVerifyClick);
		};
	}, [element, onVerifyClick]);

	useEffect(() => {
		if (!element || !onBannerChange) return;
		element.on("bannerChange", onBannerChange);
		return () => {
			element.off("bannerChange", onBannerChange);
		};
	}, [element, onBannerChange]);

	useLayoutEffect(() => {
		if (!element || !ref.current) return;
		element.mount(ref.current);
		return () => {
			element.unmount();
		};
	}, [element, ref.current]);

	return (
		<>
			<div
				style={{ ...style, visibility: isReady ? undefined : "hidden" }}
				className={className}
				ref={ref}
			/>
			{!isReady && (fallback ?? null)}
		</>
	);
}

StatusBannerElement.displayName = "StatusBannerElement";
StatusBannerElement.type = "status-banner-element" as const;
